import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Building2, MapPin, Utensils, Users, CheckCircle2, ArrowRight, Shield, HeartHandshake, Sparkles, Award, Image as ImageIcon, Layers, Phone, X, ChevronLeft, ChevronRight, Filter, MessageCircle } from "lucide-react";
import { CLINIC_TYPES, AUDIENCES, AudienceChip, EstablishmentBadge, findClinicType, findAudience } from "../lib/clinicMeta";
import QuickTriageModal from "../components/triage/QuickTriageModal";

/**
 * Lightbox de fotos — carrossel modal sobre a página inteira.
 * Navegação: setas do teclado, botões prev/next, thumbs no rodapé, ESC fecha.
 */
const PhotoLightbox = ({ photos, index, onClose, onChange }) => {
  const total = photos.length;
  const go = useCallback((delta) => {
    onChange((index + delta + total) % total);
  }, [index, total, onChange]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    // trava scroll do body enquanto aberto
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [go, onClose]);
  if (index < 0 || index >= total) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex flex-col animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      data-testid="clinic-photo-lightbox"
      onClick={onClose}
    >
      {/* topo: contador + fechar */}
      <div className="flex items-center justify-between px-4 md:px-8 py-4 text-white/90" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-mono tabular-nums" data-testid="lightbox-counter">
          {index + 1} / {total}
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Fechar"
          data-testid="lightbox-close-btn"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* palco */}
      <div className="flex-1 flex items-center justify-center px-4 md:px-16 min-h-0" onClick={(e) => e.stopPropagation()}>
        {total > 1 && (
          <button
            onClick={() => go(-1)}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Foto anterior"
            data-testid="lightbox-prev-btn"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <img
          src={photos[index]}
          alt={`Foto ${index + 1} de ${total}`}
          className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl"
          data-testid="lightbox-image"
        />
        {total > 1 && (
          <button
            onClick={() => go(1)}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Próxima foto"
            data-testid="lightbox-next-btn"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* thumbs */}
      {total > 1 && (
        <div className="px-4 md:px-8 py-4 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2 justify-center min-w-max mx-auto">
            {photos.map((url, i) => (
              <button
                key={url + i}
                onClick={() => onChange(i)}
                className={`shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                  i === index ? "border-white opacity-100 scale-105" : "border-transparent opacity-50 hover:opacity-80"
                }`}
                aria-label={`Ver foto ${i + 1}`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Vitrine PÚBLICA — anonimizada. Só código P00X + cidade + fotos + planos.
 * Toda intermediação (nome, contato, endereço) fica com o consultor RAF.
 */

export const PublicClinics = () => {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("");
  useEffect(() => {
    api.get("/public/clinics").then(({ data }) => setClinics(data)).finally(() => setLoading(false));
  }, []);

  // Contagem por tipo/público — decidimos mostrar só filtros com pelo menos 1 unidade
  // (evita a família clicar em "Hospital" e ver "0 resultados").
  const counts = useMemo(() => {
    const t = {}; const a = {};
    for (const c of clinics) {
      const et = findClinicType(c.establishment_type)?.value;
      if (et) t[et] = (t[et] || 0) + 1;
      const audArr = Array.isArray(c.audience) ? c.audience : Array.isArray(c.target_audience) ? c.target_audience : [];
      for (const raw of audArr) {
        const av = findAudience(raw)?.value;
        if (av) a[av] = (a[av] || 0) + 1;
      }
    }
    return { types: t, audiences: a };
  }, [clinics]);

  const filtered = useMemo(() => {
    return clinics.filter((c) => {
      if (typeFilter) {
        const ct = findClinicType(c.establishment_type)?.value;
        if (ct !== typeFilter) return false;
      }
      if (audienceFilter) {
        const audArr = Array.isArray(c.audience) ? c.audience : Array.isArray(c.target_audience) ? c.target_audience : [];
        const matches = audArr.some((a) => findAudience(a)?.value === audienceFilter);
        if (!matches) return false;
      }
      return true;
    });
  }, [clinics, typeFilter, audienceFilter]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Rede RAF Direct</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] mt-1">Unidades Parceiras</h1>
        <p className="text-slate-600 mt-2 text-sm text-left">
          Conheça as clínicas credenciadas que trabalham com a RAF. Identidade das unidades e contatos são revelados
          somente pelo consultor RAF durante o acolhimento, protegendo famílias e unidades.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 text-blue-800 px-3 py-2 rounded-md">
          <Shield className="w-3.5 h-3.5" />
          <span>Ao solicitar atendimento, seu consultor RAF conecta você diretamente à melhor unidade.</span>
        </div>
      </div>

      {/* Filtros — só aparecem quando há dados carregados e mais de uma unidade */}
      {!loading && clinics.length > 1 && (
        <div className="mb-6 p-4 bg-white border border-slate-200 rounded-xl" data-testid="clinic-filters">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
            <Filter className="w-3.5 h-3.5" /> Refinar por
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Tipo de estabelecimento</div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setTypeFilter("")}
                  data-testid="filter-type-all"
                  className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${!typeFilter ? "bg-[#0B2B62] border-[#0B2B62] text-white" : "bg-white border-slate-300 text-slate-600 hover:border-[#0B2B62]"}`}
                >
                  Todos ({clinics.length})
                </button>
                {CLINIC_TYPES.filter((t) => counts.types[t.value]).map((t) => {
                  const Icon = t.icon;
                  const active = typeFilter === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setTypeFilter(active ? "" : t.value)}
                      data-testid={`filter-type-${t.value.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${
                        active
                          ? `${t.bg.replace("50","600")} border-transparent text-white`.replace(/bg-(\w+)-600/, "bg-$1-700")
                          : `${t.bg} ${t.text} ${t.border} hover:brightness-95`
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {t.short} ({counts.types[t.value]})
                    </button>
                  );
                })}
              </div>
            </div>
            {Object.keys(counts.audiences).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Público atendido</div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setAudienceFilter("")}
                    data-testid="filter-audience-all"
                    className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${!audienceFilter ? "bg-[#0B2B62] border-[#0B2B62] text-white" : "bg-white border-slate-300 text-slate-600 hover:border-[#0B2B62]"}`}
                  >
                    Todos
                  </button>
                  {AUDIENCES.filter((a) => counts.audiences[a.value]).map((a) => {
                    const Icon = a.icon;
                    const active = audienceFilter === a.value;
                    return (
                      <button
                        key={a.value}
                        type="button"
                        onClick={() => setAudienceFilter(active ? "" : a.value)}
                        data-testid={`filter-audience-${a.value.toLowerCase()}`}
                        className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${
                          active
                            ? "bg-[#0B2B62] border-[#0B2B62] text-white"
                            : `${a.bg} ${a.text} ${a.border} hover:brightness-95`
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {a.label} ({counts.audiences[a.value]})
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? <div className="text-slate-500">Carregando…</div> : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-2" data-testid="clinic-empty-state">
            {clinics.length === 0 ? "Nenhuma unidade disponível publicamente ainda." : "Nenhuma unidade corresponde aos filtros selecionados."}
          </p>
          {clinics.length > 0 && (typeFilter || audienceFilter) && (
            <button onClick={() => { setTypeFilter(""); setAudienceFilter(""); }} className="mt-3 text-xs text-[#0B2B62] hover:underline" data-testid="filter-clear">
              Limpar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="public-clinics-grid">
          {filtered.map((c) => {
            const audArr = Array.isArray(c.audience) ? c.audience : Array.isArray(c.target_audience) ? c.target_audience : [];
            return (
              <Link key={c.id} to={`/unidades/${c.id}`} data-testid={`clinic-card-${c.id}`}
                className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#0B2B62] transition-all hover:shadow-md flex flex-col">
                <div className="aspect-[16/10] bg-slate-100 overflow-hidden relative">
                  {c.hero_image ? (
                    <img src={c.hero_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300"><Building2 className="w-12 h-12" /></div>
                  )}
                  {c.establishment_type && (
                    <div className="absolute top-2 left-2" data-testid={`clinic-est-${c.id}`}>
                      <EstablishmentBadge type={c.establishment_type} size="sm" />
                    </div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-widest bg-[#0B2B62] text-white px-1.5 py-0.5 rounded">{c.code}</span>
                    <h3 className="font-display font-bold text-lg text-[#0B2B62]">Clínica {c.code}</h3>
                    {c.certification_tier && (
                      <span data-testid={`clinic-tier-${c.id}`}
                        className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest ${
                          c.certification_tier === "ouro" ? "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]" :
                          c.certification_tier === "prata" ? "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900" :
                          "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white"
                        }`}>{c.certification_tier}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {c.city}, {c.state}</p>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">{c.description || "Unidade parceira credenciada da RAF Direct."}</p>
                  {audArr.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1" data-testid={`clinic-audience-${c.id}`}>
                      {audArr.slice(0, 4).map((a, i) => <AudienceChip key={`${a}-${i}`} audience={a} />)}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-[#0B2B62] font-semibold inline-flex items-center gap-1">Ver detalhes <ArrowRight className="w-3 h-3" /></div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PublicClinicDetail = () => {
  const { clinicId } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, photos: [], index: 0 });
  const [triageOpen, setTriageOpen] = useState(false);
  useEffect(() => {
    api.get(`/public/clinics/${clinicId}`).then(({ data }) => setC(data)).catch(() => setErr("Unidade não encontrada"));
  }, [clinicId]);

  const openLightbox = (photos, index) => setLightbox({ open: true, photos, index });
  const closeLightbox = () => setLightbox((s) => ({ ...s, open: false }));

  if (err) return <div className="p-10 text-center"><p className="text-slate-600">{err}</p><Link to="/unidades" className="text-[#0B2B62] hover:underline text-sm">← voltar</Link></div>;
  if (!c) return <div className="p-10 text-slate-500">Carregando…</div>;

  // Divide a descrição em parágrafos legíveis — a clínica geralmente cola um
  // bloco único com títulos embutidos ("Diferenciais", "Metodologia" etc.).
  // Quebra por "." seguido de "MaiúsculaTitulada" e limita 3 sentenças por §.
  const paragraphs = splitDescription(c.description || "");
  const heroSrc = c.hero_image || (c.gallery || [])[0];
  const certValid = c.is_certified && (!c.certification_expires_at || new Date(c.certification_expires_at) > new Date());

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      {/* Hero grande + overlay identidade */}
      <div className="relative">
        <div className="w-full aspect-[21/8] md:aspect-[21/7] bg-gradient-to-br from-[#0B2B62] to-[#164a94] overflow-hidden">
          {heroSrc && (
            <img src={heroSrc} alt="" className="w-full h-full object-cover opacity-60" data-testid="clinic-hero" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto px-6 pb-8 w-full">
            <Link to="/unidades" className="text-xs text-white/80 hover:text-white transition-colors">← todas as unidades</Link>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/90 font-bold mt-4">Unidade Parceira RAF · confidencial</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl text-white mt-2 flex items-center gap-3 flex-wrap" data-testid="clinic-name">
              Clínica {c.code}
              <span className="text-sm font-mono px-2 py-0.5 rounded-md bg-white/20 backdrop-blur border border-white/30">{c.code}</span>
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-white/90">
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {c.city}, {c.state}</span>
              {c.establishment_type && (
                <span className="inline-flex items-center gap-1"><Building2 className="w-4 h-4" /> {c.establishment_type}</span>
              )}
              {c.beds_available > 0 && (
                <span className="inline-flex items-center gap-1"><Layers className="w-4 h-4" /> {c.beds_available} vagas</span>
              )}
              {c.meals_per_day > 0 && (
                <span className="inline-flex items-center gap-1"><Utensils className="w-4 h-4" /> {c.meals_per_day} refeições/dia</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 mt-8 grid lg:grid-cols-[1fr,320px] gap-8">
        {/* Coluna principal */}
        <main className="space-y-10">
          <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg flex items-start gap-2 text-sm">
            <Shield className="w-5 h-5 shrink-0 mt-0.5" />
            <span>
              Nome, endereço e contato desta unidade são revelados exclusivamente pelo consultor RAF durante o acolhimento.
              <Link to="/pedir-ajuda" className="ml-1 font-semibold underline">Iniciar triagem</Link> para conectar-se.
            </span>
          </div>

          {(() => {
            // Deduplicação normalizada: se a clínica cadastrou "Voluntária" em
            // audience E modality (legado), mostra 1x só. Modality vence e vira chip verde.
            // Blindagem total: dados legados podem vir como string/null/objeto no lugar
            // de array — qualquer exceção aqui derrubava a página inteira (tela branca da P006).
            const norm = (s) => (typeof s === "string" ? s : String(s ?? ""))
              .toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
            const toArr = (v) => {
              if (Array.isArray(v)) return v;
              if (typeof v === "string" && v.trim()) {
                // aceita "opt1, opt2" ou "opt1;opt2" — dados legados vinham como string
                return v.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
              }
              return [];
            };
            const isStr = (x) => typeof x === "string" && x.trim().length > 0;
            const modalityArr = toArr(c.modality).filter(isStr);
            const modalitySet = new Set(modalityArr.map(norm));
            // Fallback: docs legados usam `target_audience` em vez de `audience`
            const audArr = toArr(c.audience).length ? toArr(c.audience) : toArr(c.target_audience);
            const audienceRaw = audArr.filter(isStr);
            const audienceUnique = audienceRaw.filter((a) => !modalitySet.has(norm(a)));
            const modalityUnique = Array.from(new Map(modalityArr.map((m) => [norm(m), m])).values());
            if (audienceUnique.length === 0 && modalityUnique.length === 0) return null;
            return (
              <div className="flex flex-wrap gap-2">
                {audienceUnique.map((a) => (
                  <span key={`aud-${a}`} className="text-[10px] uppercase tracking-widest bg-blue-100 text-blue-800 border border-blue-200 px-3 py-1 rounded-full font-bold">{a}</span>
                ))}
                {modalityUnique.map((m) => (
                  <span key={`mod-${m}`} className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-200 px-3 py-1 rounded-full font-bold">Internação {m}</span>
                ))}
              </div>
            );
          })()}

          {paragraphs.length > 0 && (
            <section data-testid="clinic-about">
              <SectionHeader icon={HeartHandshake} title="Sobre a unidade" />
              <div className="prose prose-slate max-w-none space-y-4 text-[15px] leading-relaxed text-slate-700 text-justify hyphens-auto">
                {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </section>
          )}

          {Array.isArray(c.features) && c.features.length > 0 && (
            <section>
              <SectionHeader icon={Sparkles} title="Estrutura & diferenciais" />
              <div className="grid sm:grid-cols-2 gap-2" data-testid="clinic-features">
                {c.features.filter((f) => typeof f === "string").map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" /> {f}
                  </div>
                ))}
                {c.meals_per_day > 0 && (
                  <div className="flex items-center gap-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <Utensils className="w-4 h-4 text-orange-500 shrink-0" /> {c.meals_per_day} refeições balanceadas ao dia
                  </div>
                )}
              </div>
            </section>
          )}

          {Array.isArray(c.activities) && c.activities.length > 0 && (
            <section>
              <SectionHeader icon={HeartHandshake} title="Programa terapêutico" />
              <div className="flex flex-wrap gap-2" data-testid="clinic-activities">
                {c.activities.filter((a) => typeof a === "string").map((a) => (
                  <span key={a} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-sm text-slate-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0B2B62] shrink-0" /> {a}
                  </span>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(c.professionals) && c.professionals.length > 0 && (
            <section>
              <SectionHeader icon={Users} title="Equipe multidisciplinar" hint="Perfis anônimos — a identidade é preservada" />
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="clinic-professionals">
                {c.professionals.filter((p) => p && typeof p === "object").map((p, i) => (
                  <div key={`${p.role || "prof"}-${p.specialty || i}`} className="p-4 bg-white border border-slate-200 rounded-xl flex gap-3 items-center hover:border-[#0B2B62] transition-colors">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-500 shrink-0">
                      <Users className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{p.role || "Profissional"}</div>
                      <div className="text-xs text-slate-500 truncate">{p.specialty || "—"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(c.plans) && c.plans.length > 0 && (
            <section>
              <SectionHeader icon={Award} title="Planos e valores" />
              <div className="grid md:grid-cols-2 gap-4" data-testid="clinic-plans">
                {c.plans.filter((p) => p && typeof p === "object").map((p, i) => (
                  <div key={p.tier || p.name || `plan-${i}`} className="bg-white p-5 border-2 border-slate-200 rounded-xl hover:border-[#0B2B62] hover:shadow-lg transition-all flex flex-col">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Plano {i + 1}</div>
                    <div className="font-display font-bold text-xl text-[#0B2B62] mt-0.5">{p.name}</div>
                    {typeof p.monthly_price === "number" && p.monthly_price > 0 && (
                      <div className="mt-2 flex items-baseline flex-wrap gap-x-1">
                        <span className="text-3xl font-bold text-slate-900 tabular-nums">R$ {p.monthly_price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
                        <span className="text-xs text-slate-400 font-normal">/mês</span>
                      </div>
                    )}
                    {p.description && <p className="text-sm text-slate-600 mt-3 whitespace-pre-line">{p.description}</p>}
                    {Array.isArray(p.includes) && p.includes.length > 0 && (
                      <ul className="mt-3 space-y-1.5 flex-1">
                        {p.includes.filter((it) => typeof it === "string").map((it) => (
                          <li key={it} className="text-xs text-slate-700 flex items-start gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" /> {it}
                          </li>
                        ))}
                      </ul>
                    )}
                    {Array.isArray(p.room_photos) && p.room_photos.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 gap-1.5">
                        {p.room_photos.filter((u) => typeof u === "string").slice(0, 3).map((url, ri) => (
                          <button
                            type="button"
                            key={url}
                            onClick={() => openLightbox(p.room_photos, ri)}
                            className="aspect-square rounded overflow-hidden bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#0B2B62]"
                            data-testid={`plan-photo-${i}-${ri}`}
                          >
                            <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(c.gallery) && c.gallery.filter((u) => typeof u === "string").length > 0 && (() => {
            const galleryClean = c.gallery.filter((u) => typeof u === "string");
            return (
            <section>
              <SectionHeader icon={ImageIcon} title="Galeria da unidade" hint={`${galleryClean.length} foto${galleryClean.length > 1 ? "s" : ""} · clique para ampliar`} />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="clinic-gallery">
                {galleryClean.map((url, gi) => (
                  <button
                    type="button"
                    key={url}
                    onClick={() => openLightbox(galleryClean, gi)}
                    className="group relative aspect-square rounded-lg overflow-hidden bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#0B2B62]"
                    data-testid={`clinic-gallery-photo-${gi}`}
                    aria-label={`Foto ${gi + 1} de ${galleryClean.length} da galeria`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                    </div>
                  </button>
                ))}
              </div>
            </section>
            );
          })()}

          {Array.isArray(c.accreditations) && c.accreditations.length > 0 && (
            <section>
              <SectionHeader icon={Shield} title="Acreditações & certificações" />
              <div className="flex flex-wrap gap-2">
                {c.accreditations.filter((a) => typeof a === "string").map((a) => (
                  <span key={a} className="text-xs bg-blue-50 text-blue-800 rounded-lg px-3 py-2 border border-blue-200 font-semibold inline-flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5" /> {a}
                  </span>
                ))}
              </div>
            </section>
          )}
        </main>

        {/* Sidebar — segue o fluxo natural da página (não é sticky).
            Sticky causava corte visual quando o card era mais alto que o
            viewport útil (viewport menos navbar fixo): o topo do card
            "Atendimento Imediato" ficava colado no navbar e escondido. */}
        <aside className="space-y-4 lg:h-fit">
          <div className="bg-white border-2 border-[#0B2B62] rounded-xl p-5 shadow-lg" data-testid="clinic-cta-card">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#0B2B62] font-bold">Fale com um consultor RAF</div>
            <h3 className="font-display font-bold text-lg text-slate-800 mt-1">Atendimento Imediato</h3>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">
              O consultor RAF avalia a situação da sua família, apresenta a unidade certa e revela os dados de contato.
            </p>
            <button type="button" onClick={() => setTriageOpen(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-[#0B2B62] hover:bg-[#0A2456] text-white font-bold rounded-lg px-4 py-3 text-sm transition-colors"
              data-testid="clinic-start-triage-btn"
            >
              <MessageCircle className="w-4 h-4" /> Falar com consultor no WhatsApp
            </button>
            <Link
              to="/pedir-ajuda"
              className="mt-2 flex items-center justify-center gap-2 text-slate-500 hover:text-[#0B2B62] text-[11px] transition-colors"
            >
              Prefiro abrir um caso completo (formulário longo)
            </Link>
          </div>

          {certValid && (
            <div className="bg-white border border-blue-200 rounded-xl p-4" data-testid="clinic-cert-seal">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center shrink-0">
                  <Award className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm text-blue-900">Credenciada RAF Direct</div>
                  {c.certification_expires_at && (
                    <div className="text-[11px] text-slate-500">Válida até {new Date(c.certification_expires_at).toLocaleDateString("pt-BR")}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-slate-100/60 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed">
            <div className="flex items-center gap-2 font-bold text-slate-800 text-sm mb-2">
              <Phone className="w-4 h-4 text-emerald-700" /> Contato via consultor
            </div>
            O contato direto é revelado após a triagem — protege a família de intermediários e garante a melhor indicação.
          </div>
        </aside>
      </div>

      {lightbox.open && lightbox.photos.length > 0 && (
        <PhotoLightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={closeLightbox}
          onChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
        />
      )}
      <QuickTriageModal open={triageOpen} onClose={() => setTriageOpen(false)}
        targetKind="clinic" targetId={c.id} targetLabel={`Clínica ${c.code}`} />
    </div>
  );
};

// Header padrão de seção — cor de accent + ícone
const SectionHeader = ({ icon: Icon, title, hint }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-[#0B2B62]" />}
      <h2 className="font-display font-bold text-xl md:text-2xl text-[#0B2B62]">{title}</h2>
    </div>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

// Quebra a descrição em parágrafos legíveis: tenta identificar títulos embutidos
// ("Diferenciais", "Metodologia", etc.) e injeta quebras antes deles. Caso não
// tenha títulos, faz split por sentenças agrupando 2-3 sentenças por §.
function splitDescription(text) {
  if (!text || typeof text !== "string") return [];
  const trimmed = text.trim();
  const headings = [
    "Diferenciais", "Metodologia", "Proposta Terapêutica", "Programa", "Estrutura",
    "Equipe", "Atendimento 24", "Tratamento", "Filosofia", "Objetivos",
    "Suporte Integral", "Prevenção", "Sobre a Unidade",
  ];
  let out = trimmed;
  for (const h of headings) {
    const re = new RegExp(`(\\.\\s+)(${h}[^.:]{0,80}[:.])`, "g");
    out = out.replace(re, "\n\n$2");
  }
  const blocks = out.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
  if (blocks.length > 1) return blocks;
  // Fallback: agrupa cada 3 sentenças
  const sentences = trimmed.split(/(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÂÊÎÔÛÃÕÇ])/);
  const paras = [];
  for (let i = 0; i < sentences.length; i += 3) {
    paras.push(sentences.slice(i, i + 3).join(" "));
  }
  return paras.filter(Boolean);
}
